import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { scaleLinear } from 'd3-scale';

interface WaveformVisualizerProps {
  data: number[];         // Array of SPL values over time
  width?: number;         // Width in pixels
  height?: number;        // Height in pixels
  color?: string;         // Waveform color
  showGradient?: boolean; // Use gradient fill
  animated?: boolean;     // Enable scroll animation
}

const WaveformVisualizer: React.FC<WaveformVisualizerProps> = ({
  data,
  width = 800,
  height = 150,
  color = '#4F46E5',
  showGradient = true,
  animated = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [displayData, setDisplayData] = useState<number[]>([]);

  // Smoothly transition to new data
  useEffect(() => {
    if (data.length > 0) {
      setDisplayData(data);
    }
  }, [data]);

  // Draw waveform on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || displayData.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size with device pixel ratio for crisp rendering
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Scale data to canvas height
    const minDb = 40;
    const maxDb = 120;
    const yScale = scaleLinear()
      .domain([minDb, maxDb])
      .range([height - 10, 10]);

    const xScale = scaleLinear()
      .domain([0, displayData.length - 1])
      .range([0, width]);

    // Create path
    ctx.beginPath();
    ctx.moveTo(0, yScale(displayData[0] || minDb));

    displayData.forEach((value, index) => {
      const x = xScale(index);
      const y = yScale(value);
      ctx.lineTo(x, y);
    });

    // If gradient, fill under the line
    if (showGradient) {
      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, `${color}80`); // 50% opacity at top
      gradient.addColorStop(1, `${color}10`); // 6% opacity at bottom

      ctx.lineTo(width, height);
      ctx.lineTo(0, height);
      ctx.closePath();
      ctx.fillStyle = gradient;
      ctx.fill();
    }

    // Draw the line
    ctx.beginPath();
    ctx.moveTo(0, yScale(displayData[0] || minDb));
    displayData.forEach((value, index) => {
      const x = xScale(index);
      const y = yScale(value);
      ctx.lineTo(x, y);
    });
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();

    // Draw data points for recent values (last 10%)
    const recentCount = Math.max(1, Math.floor(displayData.length * 0.1));
    ctx.fillStyle = color;
    displayData.slice(-recentCount).forEach((value, index) => {
      const dataIndex = displayData.length - recentCount + index;
      const x = xScale(dataIndex);
      const y = yScale(value);

      // Pulse effect on the most recent point
      if (index === recentCount - 1) {
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, 2 * Math.PI);
        ctx.fill();
      }
    });

  }, [displayData, width, height, color, showGradient]);

  // Color based on average level
  const avgLevel = displayData.length > 0
    ? displayData.reduce((a, b) => a + b, 0) / displayData.length
    : 0;

  const getColorFromLevel = (level: number): string => {
    if (level < 70) return '#10B981';
    if (level < 85) return '#F59E0B';
    if (level < 95) return '#F97316';
    return '#EF4444';
  };

  const dynamicColor = getColorFromLevel(avgLevel);

  return (
    <div className="relative" style={{ width, height }}>
      <motion.canvas
        ref={canvasRef}
        className="rounded-lg"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      />

      {/* Pulsing indicator for live data */}
      {displayData.length > 0 && animated && (
        <motion.div
          className="absolute top-2 right-2 flex items-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: dynamicColor }}
            animate={{
              scale: [1, 1.5, 1],
              opacity: [1, 0.5, 1],
            }}
            transition={{
              repeat: Infinity,
              duration: 2,
            }}
          />
          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
            LIVE
          </span>
        </motion.div>
      )}

      {/* Level indicator */}
      {displayData.length > 0 && (
        <div className="absolute bottom-2 left-2 text-xs text-gray-500 dark:text-gray-400 font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
          Avg: {avgLevel.toFixed(1)} dB
        </div>
      )}
    </div>
  );
};

export default WaveformVisualizer;
